import { useQueries, UseQueryResult } from '@tanstack/react-query';
import { IntegrationApiFeaturesCongestionReportsContractsResponsesGetCongestionReportsDetailsResponse } from '@alliander-fe/api';
import { IntegrationApi } from '@alliander-fe/api';
import { rewriteName } from '../utils/rewriteName';
import { CongestionProps } from '..';
import { isResolvedCongestion } from '../utils/isResolvedCongestion';
import { CONGESTION_DIRECTION_CONSUME, CONGESTION_DIRECTION_SUPPLY } from '../constant';

export type CongestionReportsResponse =
  IntegrationApiFeaturesCongestionReportsContractsResponsesGetCongestionReportsDetailsResponse;

export const useCongestionReports = (congestions: CongestionProps[]) => {
  const requestReports = [];

  for (const congestion of congestions) {
    if (congestion.name && !isResolvedCongestion(congestion.level.consume)) {
      requestReports.push({
        name: rewriteName(congestion.name),
        congestionType: CONGESTION_DIRECTION_CONSUME.toLowerCase(),
      });
    }

    if (congestion.name && !isResolvedCongestion(congestion.level.supply)) {
      requestReports.push({
        name: rewriteName(congestion.name),
        congestionType: CONGESTION_DIRECTION_SUPPLY.toLowerCase(),
      });
    }
  }

  const congestionReportRequests = useQueries({
    queries:
      requestReports?.map(({ name, congestionType }) => ({
        queryKey: ['congestion-reports', name, congestionType],
        enabled: Boolean(name && congestionType),
        refetchOnWindowFocus: false,
        queryFn: () => {
          return IntegrationApi.congestionReportsEndpointsGetCongestionReportsDetails({
            name,
            congestionType,
          });
        },
      })) ?? [],
  });

  const isLoading = congestionReportRequests.some((report) => report.isLoading);
  const isError = congestionReportRequests.some((report) => report.isError);
  const isSuccess = congestionReportRequests.every((report) => report.isSuccess);
  const reports = createDataObject(congestionReportRequests);

  return {
    reports,
    isLoading,
    isError,
    isSuccess,
  };
};

/**
 * This function creates an object with the postal codes as keys and the rings as values.
 * @param responseArray - The array of responses from the API.
 * @returns The object with the postal codes as keys and the rings as values.
 */
const createDataObject = (
  responseArray: UseQueryResult<CongestionReportsResponse, Error>[]
): CongestionReportsResponse[] | undefined => {
  const combineResults = responseArray
    .map((response) => {
      return response.data;
    })
    .flat()
    .filter((response) => response !== undefined);

  return combineResults;
};
